import { Box, Stack, Typography, Card, CardContent } from "@mui/material";
import AddRuleButton from "../components/AddRuleButton";
import RulesTable from "../components/RulesTable";
import { useDeleteRule, useRules } from "../hooks/useRules";
import { IRuleDto } from "../api/generated";
import { routes } from "../routes";
import { useNavigate } from "react-router";
import { useState } from "react";
import ConfirmationDialog from "../components/DialogConfirmation";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export default function RulesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: rules, isLoading, isError, error, refetch } = useRules();
  const deleteMutation = useDeleteRule();
  const [rowToDelete, setRowToDelete] = useState<IRuleDto | null>(null);
  const qc = useQueryClient();

  const onEdit = (row: IRuleDto) => {
    navigate(routes.editRule.to({ id: row.id! }));
  };

  const onDelete = (row: IRuleDto) => {
    setRowToDelete(row);
  };

  const closeConfirm = () => setRowToDelete(null);
  const confirmDelete = () => {
    if (!rowToDelete?.id) return;
    deleteMutation.mutate(rowToDelete.id, {
      onSuccess: () => {
        qc.setQueryData<IRuleDto[]>(["rules"], (prev) =>
          (prev ?? []).filter((r) => r.id !== rowToDelete.id)
        );
        setRowToDelete(null);
        refetch();
      },
    });
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#F8FAFC', p: 2, boxSizing: 'border-box' }}>
      <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid #E2E8F0', boxShadow: 'none', overflow: 'hidden', minHeight: 0 }}>
        <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', '&:last-child': { pb: 2.5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} sx={{ flexShrink: 0 }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#0F172A' }}>{t('rules.title')}</Typography>
            <AddRuleButton disabled={isError || isLoading} />
          </Stack>
          <RulesTable
            rules={rules}
            isLoading={isLoading}
            isError={isError}
            error={error}
            onEdit={onEdit}
            onDelete={onDelete}
            stretch
          />
        </CardContent>
      </Card>
      <ConfirmationDialog
          open={!!rowToDelete}
          title={t('rules.deleteTitle')}
          description={
            rowToDelete
              ? t('rules.deleteConfirm', { name: rowToDelete.name })
              : ""
          }
          loading={deleteMutation.isPending}
          confirmText={t('common.delete')}
          onConfirm={confirmDelete}
          onClose={closeConfirm}
        />
    </Box>
  );
}
